import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import DescriptionIcon from "@mui/icons-material/Description";
import {
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { sortBy } from "lodash";
import type { FC } from "react";
import type { ProviderProductType } from "../../admin/providers/providersApi";
import { formatDateWithDay } from "../../calculations2/generic/formatDateWithDay";
import { sellerByProductType } from "../../calculations2/sellerByProductType";
import type { Currency } from "../../data/BaseEntity";
import dayjs, { DATE_FORMAT, ISO_DATE_FORMAT } from "../../dayjs";
import type { PriceValue } from "../../misc/price";
import { Price } from "../../misc/price";
import type { InvoiceSellerId } from "../../orders/ordersApi";
import type { OrderFileAttachment } from "../../orders/orderTypes";
import { downloadFile } from "../associatedFiles/AssociatedFilesView";
import type { Product as ProductDetailsProduct } from "../productDetails/ProductDetails";

export interface ProductOption {
  readonly id: string;
  readonly name: string | undefined;
  readonly quantity: number;
  readonly price?: PriceValue | null;
  readonly info?: { polish?: string; english?: string };
  readonly order?: number;
}

interface OptionDetailsProps {
  readonly option: ProductOption;
  readonly currency: Currency;
  readonly exchangeRate: number;
  readonly hideProductPrices?: boolean;
  readonly quantityWidth: number;
}

const OptionDetails: FC<OptionDetailsProps> = ({
  option,
  currency,
  exchangeRate,
  hideProductPrices,
  quantityWidth,
}) => {
  const price = Price.fromNullable(option.price).multiply(exchangeRate).round();
  return (
    <TableRow
      sx={{
        "& td": {
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          py: 1.5,
          px: 2,
        },
        "&:last-child td": {
          borderBottom: "none",
        },
      }}
    >
      <TableCell sx={{ pr: 4 }}>
        <Typography variant="body2" sx={{ fontWeight: "500" }} color="text.primary">
          {option.name}
        </Typography>
        {option.info?.polish && (
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              display: "block",
              whiteSpace: "pre-line",
            }}
          >
            {option.info.polish}
          </Typography>
        )}
      </TableCell>
      {!hideProductPrices && (
        <TableCell sx={{ pr: 4 }}>
          <Typography variant="body2" color="text.secondary">
            {price.isPresent() ? `${price.value} ${currency}` : "n.d"}
          </Typography>
        </TableCell>
      )}
      <TableCell>
        <Chip
          label={option.quantity}
          size="small"
          variant="outlined"
          sx={{
            fontWeight: "bold",
            bgcolor: "#fff",
            minWidth: `${quantityWidth * 8 + 16}px`,
            justifyContent: "center",
          }}
        />
      </TableCell>
    </TableRow>
  );
};

export type Product = {
  readonly id: string;
  readonly bookingNumber?: string;
  readonly name: string | undefined;
  readonly options: ProductOption[];
  readonly date: string;
  readonly time?: string;
  readonly exchangeRate: number | undefined;
  readonly price?: Price;
  readonly note?: string;
  readonly providerName?: string;
  readonly providerProductType?: ProviderProductType;
} & Pick<ProductDetailsProduct, "description" | "contact" | "images">;

interface ProductRowProps {
  readonly product: Product;
  readonly currency: Currency;
  readonly hideProductPrices?: boolean;
  readonly files: OrderFileAttachment[];
}

type GroupedProduct = {
  product: Product;
  idx: number;
};

type ProductGroup = {
  date: string;
  products: GroupedProduct[];
};

function groupProductsByDate(products: Product[]): ProductGroup[] {
  return Object.values(
    products.reduce<Record<string, ProductGroup>>((acc, product, idx) => {
      const key = dayjs(product.date, ISO_DATE_FORMAT).format(ISO_DATE_FORMAT);

      (acc[key] ??= {
        date: product.date,
        products: [],
      }).products.push({ product, idx });

      return acc;
    }, {}),
  );
}

const ProductRows: FC<ProductRowProps> = ({ product, currency, hideProductPrices, files }) => {
  const price =
    product.price && product.exchangeRate !== undefined
      ? product.price.multiply(product.exchangeRate).round()
      : Price.onDemand;
  const file = files.find((f) => f.name == product.bookingNumber);

  const maxQuantityWidth = Math.max(...product.options.map((option) => String(option.quantity ?? "").length));
  return (
    <Box data-testid="product-row" sx={{ mb: 5, position: "relative" }}>
      {/* Nagłówek etapu */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        sx={{ mb: 2, justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" } }}
      >
        <Box>
          <Stack direction="row" spacing={1.5} sx={{ mt: 0.5, alignItems: "center" }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", color: "primary.dark" }}>
              {product.name}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <Typography variant="h6" color="text.secondary" sx={{ letterSpacing: 1, fontWeight: "bold" }}>
                {dayjs(product.date, ISO_DATE_FORMAT).format(DATE_FORMAT)}
              </Typography>
              {product.time && (
                <Stack
                  direction="row"
                  spacing={0.5}
                  sx={{
                    color: "text.secondary",
                    bgcolor: "rgba(0,0,0,0.04)",
                    px: 0.5,
                    borderRadius: 1,
                    alignItems: "center",
                  }}
                >
                  <AccessTimeIcon sx={{ fontSize: 14 }} />
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    {product.time}{" "}
                  </Typography>
                </Stack>
              )}
            </Stack>
            <Chip
              icon={<ConfirmationNumberIcon sx={{ fontSize: 14 }} />}
              label={product.bookingNumber}
              size="small"
              variant="outlined"
              color={file ? "primary" : "default"}
              onClick={
                file
                  ? (e) => {
                      e.stopPropagation();
                      return downloadFile(file.url);
                    }
                  : undefined
              }
              sx={{
                borderColor: "rgba(0,0,0,0.12)",
                fontWeight: 500,
                height: 24,
              }}
            />
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>
            Dostawca:{" "}
            <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
              {sellerLabel[sellerByProductType(product.providerProductType)]}
            </Box>
          </Typography>
        </Box>
        {!hideProductPrices && (
          <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
            <Typography variant="caption" color="text.secondary">
              Suma za etap
            </Typography>
            <Typography variant="subtitle1" color="text.primary" sx={{ fontWeight: "bold" }}>
              {price.toDisplayValue(currency)}
            </Typography>
          </Box>
        )}
      </Stack>

      {/* Pudełko ze szczegółami opcji (kajuty/pokoje) */}
      <Box
        sx={{
          bgcolor: "rgba(0,0,0,0.02)",
          borderRadius: 2,
          border: "1px solid rgba(0,0,0,0.04)",
        }}
      >
        {product.note && (
          <Box sx={{ p: 2, borderBottom: "1px solid rgba(0,0,0,0.04)" }}>
            <Stack direction="row" spacing={1} sx={{ color: "text.secondary", alignItems: "flex-start" }}>
              <DescriptionIcon fontSize="small" sx={{ mt: 0.2 }} />
              <Typography variant="body2">{product.note}</Typography>
            </Stack>
          </Box>
        )}
        <TableContainer sx={{ borderRadius: 2 }}>
          <Table size="small" sx={{ width: "auto" }}>
            <TableHead>
              <TableRow sx={{ "& th": { borderBottom: "1px solid rgba(0,0,0,0.06)" } }}>
                <TableCell sx={{ borderBottom: "1px solid rgba(0,0,0,0.06)", p: 2, pr: 4 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: "bold", textTransform: "uppercase" }}
                  >
                    Wybrana opcja
                  </Typography>
                </TableCell>
                {!hideProductPrices && (
                  <TableCell sx={{ borderBottom: "1px solid rgba(0,0,0,0.06)", p: 2, pr: 4 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: "bold", textTransform: "uppercase" }}
                    >
                      Cena jedn.
                    </Typography>
                  </TableCell>
                )}
                <TableCell sx={{ borderBottom: "1px solid rgba(0,0,0,0.06)", p: 2 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: "bold", textTransform: "uppercase" }}
                  >
                    Ilość
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortBy(product.options, (p) => p.order ?? Number.MAX_SAFE_INTEGER).map((item, iIdx) => (
                <OptionDetails
                  option={item}
                  key={iIdx}
                  hideProductPrices={hideProductPrices}
                  currency={currency}
                  exchangeRate={product.exchangeRate ?? 1}
                  quantityWidth={maxQuantityWidth}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

interface Props {
  readonly values: Product[];
  readonly currency: Currency;
  readonly hideProductPrices?: boolean;
  readonly totalPrice: Price;
  readonly orderId?: string;
  readonly files: OrderFileAttachment[];
}

const ProductsTable: FC<Props> = ({ values, currency, hideProductPrices, totalPrice, files }) => {
  const groupedProducts = groupProductsByDate(values);
  return (
    <Box sx={{ ml: { xs: 2, md: 3 }, borderLeft: "2px dashed #e0e0e0", pl: { xs: 3, md: 5 }, position: "relative" }}>
      {groupedProducts.map((group) => (
        <Box key={group.date} sx={{ mb: 5, position: "relative" }}>
          {/* Kropka osi czasu */}
          <Box
            sx={{
              position: "absolute",
              left: { xs: -25, md: -41 },
              transform: "translateX(-50%)",
              top: 4,
              width: 14,
              height: 14,
              borderRadius: "50%",
              bgcolor: "primary.main",
              border: "3px solid #fff",
              boxShadow: "0 0 0 1px #ccc",
            }}
          />

          <Typography
            component="span"
            variant="subtitle1"
            sx={{
              display: "inline-block",
              color: "text.primary",
              fontWeight: "bold",
              bgcolor: "rgba(0,0,0,0.04)",
              px: 1,
              py: 0.3,
              borderRadius: 1,
              mb: 2,
            }}
          >
            {formatDateWithDay(group.date)}
          </Typography>

          {group.products.map(({ product, idx }) => (
            <ProductRows
              key={`${idx}:${product.id}:${product.date}`}
              product={product}
              currency={currency}
              hideProductPrices={hideProductPrices}
              files={files}
            />
          ))}
        </Box>
      ))}
      {/* PODSUMOWANIE NA SAMYM DOLE OSI */}
      {!hideProductPrices && (
        <Box sx={{ position: "relative", mt: 2 }}>
          {/* Ostatnia, podsumowująca zielona kropka */}
          <Box
            sx={{
              position: "absolute",
              left: { xs: -25, md: -41 },
              transform: "translateX(-50%)",
              top: 24,
              width: 14,
              height: 14,
              borderRadius: "50%",
              bgcolor: "success.main",
              border: "3px solid #fff",
              boxShadow: "0 0 0 1px #ccc",
            }}
          />

          <Stack
            direction="row"
            sx={{
              bgcolor: "rgba(46, 125, 50, 0.05)",
              p: 3,
              borderRadius: 2,
              border: "1px dashed rgba(46, 125, 50, 0.3)",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="subtitle1" color="success" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
              Łącznie za usługi
            </Typography>
            <Typography variant="h5" color="success" sx={{ fontWeight: "bold" }}>
              {totalPrice.toDisplayValue(currency)}
            </Typography>
          </Stack>
        </Box>
      )}
    </Box>
  );
};

const sellerLabel: Record<InvoiceSellerId, string> = {
  B2B_HOTELS: "B2Bhotels",
  PRZEZMORZE: "Przezmorze.pl",
};

export default ProductsTable;
